import { Suspense, type ReactElement } from 'react';
import { DEFAULT_SLOT_ORDER, frontendSkillRegistry } from './registry';

interface SkillSlotProps {
  name: string;
  /** Properties, die jedem Renderer durchgereicht werden. */
  context?: Record<string, unknown>;
  /** Fallback, wenn kein Skill den Slot bedient. */
  fallback?: ReactElement | null;
  /**
   * Untere/obere Grenze des `order`-Fensters (beide inklusiv), das dieser
   * Aufruf rendert. Damit kann eine Seite denselben Slot mehrfach einsetzen
   * und eigene Karten dazwischen legen — ein Skill wählt über sein `order`,
   * in welchen Block er fällt, ohne dass die Seite seinen Key kennen muss.
   */
  minOrder?: number;
  maxOrder?: number;
}

/**
 * Rendert alle Slot-Einträge eines benannten Slots in definierter Reihenfolge.
 * Beispiel: <SkillSlot name="project-detail.sidebar" context={{ projectId }} />
 *
 * Slot-Renderer dürfen `React.lazy`-Komponenten sein — Skills laden ihren
 * schweren UI-Code so erst, wenn der Slot tatsächlich rendert. Jeder Eintrag
 * bekommt daher eine eigene Suspense-Boundary; Fallback ist bewusst `null`,
 * weil Slot-Inhalte Sekundär-UI sind (ein Spinner pro Panel wäre unruhig).
 */
export function SkillSlot({
  name,
  context,
  fallback = null,
  minOrder,
  maxOrder,
}: SkillSlotProps): ReactElement {
  const entries = frontendSkillRegistry.entriesForSlot(name).filter((entry) => {
    const order = entry.order ?? DEFAULT_SLOT_ORDER;
    if (minOrder !== undefined && order < minOrder) return false;
    if (maxOrder !== undefined && order > maxOrder) return false;
    return true;
  });
  if (entries.length === 0) return fallback ?? <></>;
  return (
    <>
      {entries.map((entry) => {
        const Renderer = entry.render;
        return (
          <Suspense key={`${name}:${entry.key}`} fallback={null}>
            <Renderer {...(context ?? {})} />
          </Suspense>
        );
      })}
    </>
  );
}
